import React, { useMemo } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import type { ActionRequiredWaitInterval } from '../../utils/actionRequiredWaitBreakdown';
import { buildActionRequiredWaitDailyChartData } from '../../utils/actionRequiredWaitChartData';
import './ActionRequiredWaitTimelineChart.css';

function formatTooltipMinutes(v: number): string {
  if (v < 60) return `${Math.round(v)}m`;
  const h = Math.floor(v / 60);
  const m = Math.round(v % 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

const ActionRequiredWaitTimelineChart: React.FC<{ intervals: ActionRequiredWaitInterval[] }> = ({
  intervals,
}) => {
  const { chartData, series, empty } = useMemo(
    () => buildActionRequiredWaitDailyChartData(intervals),
    [intervals],
  );

  if (empty) {
    return (
      <div className="pm-wait-timeline-chart pm-wait-timeline-chart--empty">
        <p>No dated wait history yet — action required changes will appear over time.</p>
      </div>
    );
  }

  return (
    <div className="pm-wait-timeline-chart">
      <p className="pm-wait-timeline-chart-hint">
        Wait time per day by holder (stacked). Hover for totals.
      </p>
      <div className="pm-wait-timeline-chart-inner">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 8, right: 10, left: 6, bottom: 22 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
            <XAxis
              dataKey="dateLabel"
              tick={{ fill: 'var(--pm-text-muted, #6b7280)', fontSize: 9 }}
              tickLine={false}
              axisLine={{ stroke: 'var(--pm-border-subtle, rgba(0,0,0,0.08))' }}
              interval="preserveStartEnd"
              minTickGap={28}
              angle={-32}
              textAnchor="end"
              height={48}
            />
            <YAxis
              tick={{ fill: 'var(--pm-text-muted, #6b7280)', fontSize: 10 }}
              tickLine={false}
              axisLine={{ stroke: 'var(--pm-border-subtle, rgba(0,0,0,0.08))' }}
              tickFormatter={v => (v >= 60 ? `${Math.round(v / 60)}h` : `${v}m`)}
              width={52}
              dx={-2}
            />
            <Tooltip
              contentStyle={{
                background: '#111827',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 8,
                fontSize: 11,
              }}
              labelStyle={{ color: '#e5e7eb', marginBottom: 4 }}
              formatter={(value, name) => [
                formatTooltipMinutes(typeof value === 'number' ? value : Number(value) || 0),
                String(name),
              ]}
              labelFormatter={label => String(label)}
            />
            <Legend
              wrapperStyle={{ fontSize: 10, paddingTop: 4 }}
              formatter={value => (
                <span style={{ color: 'var(--pm-text-secondary, #4b5563)' }}>{value}</span>
              )}
            />
            {series.map(s => (
              <Bar
                key={s.key}
                dataKey={s.key}
                name={s.displayName}
                stackId="1"
                fill={s.color}
                isAnimationActive={true}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ActionRequiredWaitTimelineChart;
